import { useRef } from 'react';
import * as Headless from '@headlessui/react';
import {
  ArrowUpTrayIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

import type { MissingDocument } from '@/modules/documents/domain/entities/document';
import { label } from '@/utils/format/label';

export type MissingDocumentsDisclosureProps = {
  documents: MissingDocument[];
  onItemClick: (document: MissingDocument) => void;
};

export function MissingDocumentsDisclosure({
  documents,
  onItemClick,
}: MissingDocumentsDisclosureProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  function handleOnCollapseListClick() {
    if (buttonRef.current) {
      buttonRef.current.click();
      buttonRef.current.scrollIntoView();
    }
  }

  return (
    <div className="rounded p-5 ring-1 ring-neutral-300">
      <Headless.Disclosure>
        <Headless.Disclosure.Button
          ref={buttonRef}
          className="flex flex-col gap-3 text-zinc-900 ui-open:pb-3"
        >
          <div className="flex items-center">
            <ExclamationCircleIcon className="mr-2 size-6 text-primary-600" />
            <h2>Please upload the required missing documents</h2>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <ChevronRightIcon className="size-3 transition-transform duration-300 ui-open:rotate-90" />
            {label(
              ['1 missing document', `${documents.length} missing documents`],
              documents.length,
            )}
          </div>
        </Headless.Disclosure.Button>
        <Headless.Disclosure.Panel>
          {documents.map((document) => (
            <MissingDocumentRow
              key={document.id}
              document={document}
              onClick={() => onItemClick(document)}
            />
          ))}
          <div
            className="mt-4 cursor-pointer text-xs text-zinc-500 hover:text-zinc-600"
            onClick={handleOnCollapseListClick}
          >
            Collapse list
          </div>
        </Headless.Disclosure.Panel>
      </Headless.Disclosure>
    </div>
  );
}

type MissingDocumentRowProps = {
  document: MissingDocument;
  onClick: () => void;
};

function MissingDocumentRow({ document, onClick }: MissingDocumentRowProps) {
  return (
    <div
      className="group flex cursor-pointer gap-1 border-b border-neutral-300 py-4 pr-4 hover:bg-neutral-100"
      onClick={onClick}
    >
      <div className="flex px-3">
        <ArrowUpTrayIcon className="size-4 text-zinc-500 opacity-0 group-hover:opacity-100" />
      </div>
      <div className="flex grow flex-col">
        <div className="flex justify-between">
          <div>{document.name}</div>
          <div className="rounded-full bg-gray-500 px-3 py-1 text-sm text-white">
            {document.type}
          </div>
        </div>
        <div className="flex gap-4 text-xs text-neutral-500">
          {document.tags.map((tag) => (
            <span key={tag}>#{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
